import axios, { AxiosResponse } from "axios";
import { BalanceResponseDto } from "../features/finance/financeApi";
import { Transaction } from "../types/transaction";
import { UserForTable } from "../types/user";
export const baseHref = process.env.REACT_APP_API_BASE_HREF || "/api";
axios.defaults.withCredentials = true;
export const endPoint = (
  type: string,
  query: Record<string, any> | null = null
) => {
  const url = `${baseHref}${type}`;

  if (query) {
    const params = new URLSearchParams({
      ...query,
    });
    return `${url}?${params.toString()}`;
  }

  return url;
};
export let requestPayload: Record<string, any> = {};

export const getPreviousRequestPayload = (path: string) => {
  return requestPayload[endPoint(path)];
};

axios.interceptors.request.use((request) => {
  if (request.url) requestPayload[request.url] = request.data;
  return request;
});

axios.interceptors.response.use((response) => {
  if (response.config.url && response?.data?.error?.code !== 1017) {
    requestPayload[response.config.url] = undefined;
  }

  return response;
});

export const extract = (r: AxiosResponse | any) => r.data;

export type ParseData = {
  result?: boolean;
  errors?: string[];
  error?: {
    code: number;
    message: string;
  };
  list?: string[];
  files?: string[];
  missingFields?: string[];
  [key: string]: any;
};

export const parseResult = (data: ParseData) => {
  if (!data.result) {
    if (data.errors && Array.isArray(data.errors)) {
      throw data.errors.join("; ");
    }

    throw data.error;
  }

  return data as any;
};

export const getAccount = async () => {
  const r = await axios.get(endPoint("/account"));
  return extract(r);
};

export const login = async (login: string, password: string, otp?: string) => {
  const r = await axios.post(endPoint("/login"), { login, password, otp });
  const data = await extract(r);
  return parseResult(data);
};

export const fetchWalletTypes = async () => {
  const r = await axios.get(endPoint("/wallet/types"));
  return extract(r);
};

export type SignUpParams = {
  email: string;
  first_name: string;
  last_name: string;
  wallet_type_id: string;
  name: string;
  company_email: string;
  company_number: string;
  phone: string;
  website: string;
  first_name_director: string;
  last_name_director: string;
  country: string;
  city: string;
  street: string;
  postal_code: string;
  description: string;
  password: string;
};

export type SignUpWithReferralParams = {
  email: string;
  first_name: string;
  last_name: string;
  kyc_program_id: number | null;
  phone: string;
  country: string;
  citizenship_country: string;
  user_birth_date: string;
  password: string;
  gender: string;
  currency: string;
  city: string;
  state: string;
  zip_code: string;
  address: string;
  form1_json: string | null;
  form3_json: string | null;
  form4_json: string | null;
  recaptchaToken?: string | undefined | null;
  ui_language?: string;
  agreement_consents?: Record<string, string>;
};

export const signUpWithReferral = async ({
  email,
  first_name,
  last_name,
  kyc_program_id,
  phone,
  country,
  citizenship_country,
  user_birth_date,
  password,
  gender,
  currency,
  city,
  state,
  zip_code,
  address,
  form1_json,
  form3_json,
  form4_json,
  recaptchaToken,
  ui_language,
  agreement_consents,
}: SignUpWithReferralParams) => {
  const r = await axios.post(endPoint("/new-sign-up"), {
    email,
    first_name,
    last_name,
    kyc_program_id,
    phone,
    country,
    citizenship_country,
    user_birth_date,
    password,
    gender,
    currency,
    city,
    state,
    zip_code,
    address,
    form1_json,
    form3_json,
    form4_json,
    recaptchaToken,
    ui_language,
    agreement_consents,
  });
  const data = await extract(r);
  return parseResult(data);
};

export interface CheckCredsParams {
  email: string;
  phone: string;
}

export const checkCreds = async ({ email, phone }: CheckCredsParams) => {
  const url = endPoint("/check-creds", { email, phone });

  const response = await axios.get(url);
  const data = await extract(response);
  return parseResult(data) as { result: boolean; message?: string };
};

export const checkEmailForDuplicate = async ({ email }: { email: string }) => {
  const url = endPoint("/check-email-for-duplicates", { email });

  const response = await axios.get(url);
  const data = await extract(response);
  return parseResult(data) as { result: boolean; message?: string };
};

export interface SignUpExtendedParams {
  email: string;
  phone: string;
  password: string;
  kyc_program_id: number;
}

export const signUpExtended = async ({
  email,
  phone,
  password,
  kyc_program_id,
}: SignUpExtendedParams) => {
  const url = endPoint("/new-sign-up-iledgends");
  const r = await axios.post(url, { email, phone, password, kyc_program_id });
  const data = await extract(r);
  return parseResult(data);
};

export type UpdateAccountParams = Partial<{
  form1_json: string | null;
  form2_json: string | null;
  form3_json: string | null;
  form4_json: string | null;
  first_name: string | null;
  last_name: string | null;
  date_of_birth: string | null;
  country: string | null;
  citizenship_country: string | null;
  gender: string | null;
  city: string | null;
  state: string | null;
  zip_code: string | null;
  address: string | null;
}>;

export const updateUserAccount = async (params: UpdateAccountParams) => {
  const r = await axios.put(endPoint("/update-user"), params);
  const data = extract(r);
  return parseResult(data);
};

export const updateUserLang = async (new_language: string) => {
  const r = await axios.put(endPoint("/update-lang"), { new_language });
  const data = extract(r);
  return parseResult(data);
};

export const signUp = async ({
  email,
  first_name,
  last_name,
  wallet_type_id,
  name,
  company_email,
  company_number,
  phone,
  website,
  first_name_director,
  last_name_director,
  country,
  city,
  street,
  postal_code,
  description,
  password,
}: SignUpParams) => {
  const r = await axios.post(endPoint("/sign-up"), {
    email,
    first_name,
    last_name,
    wallet_type_id: wallet_type_id === "Private Wallet" ? "0" : "1",
    name,
    company_email,
    company_number,
    phone,
    website,
    first_name_director,
    last_name_director,
    country,
    city,
    street,
    postal_code,
    description,
    password,
  });
  const data = await extract(r);
  return parseResult(data);
};

export type RequestPassRecoveryParams = {
  email: string;
};
export const requestPassRecovery = async ({
  email,
}: RequestPassRecoveryParams) => {
  const r = await axios.post(endPoint("/request/passrecovery"), { email });
  const data = await extract(r);
  return parseResult(data);
};

export type SetPasswordParams = {
  password: string;
  token: string;
};
export const setPassword = async ({ token, password }: SetPasswordParams) => {
  const r = await axios.post(endPoint("/recover_password"), {
    password,
    token,
  });
  const data = await extract(r);
  return parseResult(data);
};

export type ConfirmPhoneParams = {
  code: string;
  clientId: string;
};
export const confirmPhone = async ({ code, clientId }: ConfirmPhoneParams) => {
  const r = await axios.post(endPoint("/confirm-phone"), {
    code,
    clientId,
  });
  const data = await extract(r);
  return parseResult(data);
};

export const getDefaultKycPrograms = async () => {
  const r = await axios.get(endPoint("/get-default-kyc-programs"));
  const data = await extract(r);
  return parseResult(data);
};

export const getKycProgramById = async (id: number) => {
  const r = await axios.get(endPoint(`/get-program/${id}`));
  const data = await extract(r);
  return parseResult(data);
};

export const confirmEmail = async (token: string) => {
  const r = await axios.get(endPoint(`/new-confirm-email/${token}`));
  const data = extract(r);
  return data;
};

export const fetchManualSellTransaction = async (transactionId: number) => {
  const r = await axios.get(
    endPoint(`/transaction?transactionId=${transactionId}`)
  );
  const data = extract(r);
  return data;
};

export const saveCustomerBlockchainLink = async (input: {
  transactionId: number;
  customer_blockchain_link: string;
}) => {
  const r = await axios.post(endPoint(`/save-customer-blockchain-link`), input);
  const data = extract(r);
  return data;
};

export type QuestionnaireOption = {
  value: string;
  label: string;
};

export type QuestionnaireQuestionType =
  | "select"
  | "radio"
  | "checkbox"
  | "text";

export type QuestionnaireQuestion = {
  code: string;
  label: string;
  text?: string;
  type: QuestionnaireQuestionType;
  required: boolean;
  order: number;
  options?: QuestionnaireOption[];
  freeTextOnValue?: string;
};

export type QuestionnaireDefinition = {
  questions: QuestionnaireQuestion[];
};

export type QuestionnaireDto = {
  id: number;
  name: string | null;
  active: boolean;
  order: number;
  definition: QuestionnaireDefinition | null;
};

export type QuestionnaireSubmission = {
  questionnaireId?: number;
  answers: Record<string, string>;
};

export const getQuestionnaires = async (): Promise<QuestionnaireDto[]> => {
  const r = await axios.get(endPoint(`/questionnaires`));
  const data = extract(r);
  return (data?.questionnaires as QuestionnaireDto[]) || [];
};

export const questionnaireAnswered = async (
  submission: QuestionnaireSubmission
) => {
  const r = await axios.post(endPoint(`/questionnaire_answered`), submission);
  const data = extract(r);
  if (!data?.result) {
    throw data?.error || new Error("Failed to save questionnaire answers");
  }
  return data;
};

export const sendSmsCode = async (clientId: string) => {
  const r = await axios.get(endPoint(`/send-sms-code/${clientId}`));
  const data = extract(r);
  return data;
};

export const getDefaultKycProgram = async () => {
  const r = await axios.get(endPoint(`/get-default-kyc-program`));
  const data = extract(r);
  return data;
};

export const getSumSubWebSdkLink = async () => {
  const r = await axios.get(endPoint(`/start-sum-sub-kyc-verification`));
  const data = extract(r);
  return data;
};

export const getSumSubAccessToken = async (flowName: string) => {
  const r = await axios.get(
    endPoint(`/generate-sum-sub-access-token?flowName=${flowName}`)
  );
  const data = extract(r);
  return data;
};

export const generateGetIdPersonalVerificationLink = async (
  flowName: string
) => {
  const r = await axios.get(
    endPoint(`/generate-get-id-verification-link?flowName=${flowName}`)
  );
  const data = extract(r);
  return data;
};

export const fetchBalances = async () => {
  const r = await axios.get(endPoint("/finance/balances"));
  const data = await extract(r);
  return parseResult(data);
};

export const getLastAddress = async (asset: string) => {
  const r = await axios.get(endPoint(`/finance/address/${asset}`));
  return extract(r);
};

export const getNewAddress = async (asset: string) => {
  const response = await axios.post(endPoint(`/finance/address/${asset}`));
  return extract(response);
};

export const getNewAddressForManualSell = async (asset: string) => {
  const response = await axios.get(
    endPoint(`/exchange/get-manual-sell-address/${asset}`)
  );

  return extract(response);
};

export const getMaxWithdrawAmount = (fee: number, asset = "btc") =>
  axios
    .get(endPoint("/get_withdraw_all_values", { asset, fee }))
    .then(extract)
    .then(parseResult);

export const fetchTransactions = async (
  asset: string,
  count = 100,
  offset = 0,
  filter: Record<string, any> = {}
) => {
  const r = await axios.get(
    endPoint(`/finance/transactions/${asset}`, { count, offset, ...filter })
  );
  return extract(r);
};

export const checkStatusOfPayment = async (orderId: string) => {
  const r = await axios.get(
    endPoint(`/paytechno/check/status?orderId=${orderId}`)
  );
  return extract(r);
};

export const fetchFiatTransactions = async (
  asset: string,
  count = 100,
  offset = 0
) => {
  const r = await axios.get(
    endPoint(`/finance/transactions/fiat/${asset}`, { count, offset })
  );
  return extract(r);
};

export const fetchFeeTxs = async (asset: string, count = 100, offset = 0) => {
  const r = await axios.get(
    endPoint(`/fee_transactions/${asset}`, { count, offset })
  );
  return extract(r);
};

export const fetchAdminBalances = async () => {
  const r = await axios.get(endPoint("/admin/balances"));
  return extract(r);
};

export type FiatFromCardParams = {
  amount: number;
  currency: string;
  phone: string;
  street: string;
  city: string;
  zip: string;
  state: string;
  country: string;
};

export const fiatFromCard = async ({
  currency,
  amount,
  phone,
  street,
  city,
  zip,
  state,
  country,
}: FiatFromCardParams) => {
  const r = await axios.post(endPoint("/paytechno/fiat"), {
    currency,
    amount,
    phone,
    street,
    city,
    zip,
    state,
    country,
  });
  const data = await extract(r);
  return parseResult(data);
};

export const exchangeBuy = async (params: Record<string, any>) => {
  const r = await axios.post(endPoint("/exchange/buy"), params);
  const data = await extract(r);
  return parseResult(data);
};

export const exchangeSell = async (params: Record<string, any>) => {
  const r = await axios.post(endPoint("/exchange/sell"), params);
  const data = await extract(r);
  return parseResult(data);
};

export const logout = async () => {
  const r = await axios.post(endPoint("/logout"));
  return extract(r);
};

export const requestOtpCode = async () => {
  const r = await axios.get(endPoint("/request-otp-code"));
  return extract(r);
};

export const updateCompany = async (
  id: number,
  toUpdate: Record<string, any>
) => {
  const r = await axios.put(endPoint(`/company/${id}`), toUpdate);
  const data = await extract(r);
  return parseResult(data);
};

export const updateUser = async (toUpdate: Record<string, any>) => {
  let data: Record<string, any> = {};
  if (toUpdate.first_name) {
    data.first_name = toUpdate.first_name;
  }
  if (toUpdate.last_name) {
    data.last_name = toUpdate.last_name;
  }
  if (toUpdate.phone) {
    data.phone = toUpdate.phone;
  }
  if (toUpdate.otp_enabled) {
    data.otp_enabled = toUpdate.otp_enabled;
  }
  if (toUpdate.otp) {
    data.otp = toUpdate.otp;
  }
  if (toUpdate.avatar) {
    data.avatar = toUpdate.avatar;
  }
  if (toUpdate.id_doc) {
    data.id_doc = toUpdate.id_doc;
  }
  if (toUpdate.address_doc) {
    data.address_doc = toUpdate.address_doc;
  }
  if (toUpdate.toc_approved) {
    data.toc_approved = toUpdate.toc_approved;
  }
  if (toUpdate.sms_code) {
    data.sms_code = toUpdate.sms_code;
  }

  if (toUpdate.country) {
    data.country = toUpdate.country;
  }

  if (toUpdate.country) {
    data.citizenship_country = toUpdate.citizenship_country;
  }

  if (toUpdate.city) {
    data.city = toUpdate.city;
  }

  if (toUpdate.currency) {
    data.currency = toUpdate.currency;
  }

  if (toUpdate.state) {
    data.state = toUpdate.state;
  }

  if (toUpdate.zip_code) {
    data.zip_code = toUpdate.zip_code;
  }

  if (toUpdate.address) {
    data.address = toUpdate.address;
  }

  if (toUpdate.gender) {
    data.gender = toUpdate.gender;
  }

  if (toUpdate.user_birth_date) {
    data.user_birth_date = toUpdate.user_birth_date;
  }

  if (Object.keys(data).length === 0) {
    return Promise.resolve({});
  }

  const r = await axios.post(endPoint("/account"), data);
  const data_1 = await extract(r);
  return parseResult(data_1);
};

export type UpdatePasswordParams = {
  old_password: string;
  password: string;
};
export const updatePassword = async ({
  password,
  old_password,
}: UpdatePasswordParams) => {
  const r = await axios.post(endPoint("/account/password"), {
    password,
    old_password,
  });
  const data = await extract(r);
  return parseResult(data);
};

export type SendFundsParams = {
  asset: string;
  amount: number;
  finalAmountToSend: number;
  address: string;
  otp?: string;
  fee?: number;
  replaceable?: boolean;
  subtract?: boolean;
  counterparty_id?: number | null;
};

export const sendFunds = async ({
  asset,
  amount,
  finalAmountToSend,
  address,
  otp,
  fee,
  replaceable,
  subtract = false,
  counterparty_id = null,
}: SendFundsParams) => {
  const r = await axios.post(endPoint("/finance/send"), {
    asset,
    amount,
    finalAmountToSend,
    address,
    otp,
    fee,
    subtract,
    replaceable,
    counterparty_id,
  });
  const data = await extract(r);
  return parseResult(data);
};

export type EstimateWithdrawalParams = {
  asset: string;
  toAddress: string;
  amount: string;
  feeType?: "min" | "mid" | "max";
  fromAddress?: string;
};

export const estimateWithdrawal = async (params: EstimateWithdrawalParams) => {
  const r = await axios.post(
    endPoint("/withdrawal-estimation/estimate"),
    params
  );
  const data = await extract(r);
  if (data.result === false) {
    throw data.error || { message: "Failed to get withdrawal estimation" };
  }
  return data;
};

export type ConfirmWithdrawalParams = {
  asset: string;
  toAddress: string;
  amount: string;
  requestedAmount?: string;
  feeType?: "min" | "mid" | "max";
  fromAddress?: string;
  otp?: string;
  systemFee?: string;
  networkFee?: string;
};

export const confirmWithdrawal = async (params: ConfirmWithdrawalParams) => {
  const r = await axios.post(
    endPoint("/withdrawal-estimation/confirm"),
    params
  );
  const data = await extract(r);
  return parseResult(data);
};

export const downloadDocWalletFile = (doc_wallet_id: string) => {
  return axios.get(endPoint(`/get_doc_wallet_file/${doc_wallet_id}`), {
    responseType: "blob",
  });
};
export const fetchFeeRates = async (asset: string) => {
  const r = await axios.get(endPoint(`/fees`, { asset }));
  const data = await extract(r);
  return parseResult(data);
};

export type GetRequiredTransactionDocumentsParams = {
  transId: number;
};

export const getRequiredTransactionDocuments = async ({
  transId,
}: GetRequiredTransactionDocumentsParams) => {
  const r = await axios.get(
    endPoint("/get-required-transaction-documents", { trans_id: transId })
  );
  const data = await extract(r);
  return parseResult(data);
};

export type UploadKycFileParams = {
  file_type: string;
  base64: string;
  assign_id: string;
  document_id: string;
  trans_id?: string;
};

export const uploadKycFile = async ({
  file_type,
  base64,
  assign_id,
  document_id,
  trans_id,
}: UploadKycFileParams) => {
  const payload = {
    file_type,
    base64,
    assign_id,
    document_id,
    trans_id,
  };
  const r = await axios.post(endPoint("/upload-file"), payload);
  const data = await extract(r);
  return parseResult(data);
};

export const uploadFile = async (name: string, file: string) => {
  const formData = new FormData();
  formData.append(name, file);

  const r = await axios.post(endPoint("/upload"), formData, {
    headers: {
      "Content-Type": "multipart/form-data",
    },
  });
  const data = await extract(r);
  const res = await parseResult(data);
  if (res.files) {
    // @ts-ignore
    return res?.files[name];
  }
  return undefined;
};

export const uploadExcelFile = async (file: string) => {
  const r = await axios.post(
    endPoint("/exchange/upload-excel-for-batch-transactions"),
    { file }
  );
  const data = await extract(r);
  const res = await parseResult(data);
  return res;
};

export const uploadExcelFileWalletMode = async (
  file: string,
  otp?: string
): Promise<FireblocksBatchUploadResult> => {
  try {
    const r = await axios.post(
      endPoint("/exchange/upload-excel-for-batch-transactions-wallet-mode"),
      { file, otp }
    );
    const data = extract(r) as Partial<FireblocksBatchUploadResult> & {
      error?: { code: number; message: string };
    };
    if (!data.result) {
      throw data.error;
    }
    return {
      result: true,
      total: data.total || 0,
      sentCount: data.sentCount || 0,
      errorCount: data.errorCount || 0,
      sent: data.sent || [],
      errors: data.errors || [],
    };
  } catch (e: any) {
    throw e?.response?.data?.error || e;
  }
};

export type FireblocksBatchSentRow = {
  row: number;
  transactionId: number;
  asset: string;
  destinationAddress: string;
  amountCrypto: string;
};

export type FireblocksBatchErrorRow = {
  row: number;
  destinationAddress?: string;
  asset?: string;
  error: string;
  missingFields?: string[];
};

export type FireblocksBatchUploadResult = {
  result: boolean;
  total: number;
  sentCount: number;
  errorCount: number;
  sent: FireblocksBatchSentRow[];
  errors: FireblocksBatchErrorRow[];
};

export type BatchCounterpartyCreatedRow = {
  row: number;
  id: number;
  type: string;
  payment_method_account_id: string;
  chain: string;
};

export type BatchCounterpartyErrorRow = {
  row: number;
  payment_method_account_id?: string;
  error: string;
};

export type BatchCounterpartyUploadResult = {
  result: boolean;
  total: number;
  createdCount: number;
  errorCount: number;
  created: BatchCounterpartyCreatedRow[];
  errors: BatchCounterpartyErrorRow[];
};

export const uploadExcelFileBatchCounterparty = async (
  file: string
): Promise<BatchCounterpartyUploadResult> => {
  try {
    const r = await axios.post(
      endPoint("/upload-excel-for-batch-counterparties"),
      { file }
    );
    const data = extract(r) as Partial<BatchCounterpartyUploadResult> & {
      error?: { code: number; message: string };
    };
    if (!data.result) {
      throw data.error;
    }
    return {
      result: true,
      total: data.total || 0,
      createdCount: data.createdCount || 0,
      errorCount: data.errorCount || 0,
      created: data.created || [],
      errors: data.errors || [],
    };
  } catch (e: any) {
    throw e?.response?.data?.error || e;
  }
};

export const uploadExcelFileFireblocksBatch = async (
  file: string,
  otp?: string
): Promise<FireblocksBatchUploadResult> => {
  try {
    const r = await axios.post(
      endPoint("/exchange/upload-excel-for-fireblocks-batch"),
      { file, otp }
    );
    const data = extract(r) as Partial<FireblocksBatchUploadResult> & {
      error?: { code: number; message: string };
    };
    if (!data.result) {
      throw data.error;
    }
    return {
      result: true,
      total: data.total || 0,
      sentCount: data.sentCount || 0,
      errorCount: data.errorCount || 0,
      sent: data.sent || [],
      errors: data.errors || [],
    };
  } catch (e: any) {
    throw e?.response?.data?.error || e;
  }
};

export const getTemplate = (name: string) =>
  axios.get(endPoint(`/templates/${name}`), {
    responseType: "blob",
  });

export const getOtpToken = () =>
  axios.get(endPoint("/account/otp")).then(extract).then(parseResult);

export const fetchApiData = async (otp = "") => {
  const r = await axios.get(endPoint("/account/api_data", { otp }));
  return extract(r);
};
export const fetchGenerateKeys = async (otp: string) => {
  const r = await axios.post(endPoint("/account/generate_keys"), { otp });
  const data = await extract(r);
  return parseResult(data);
};
export const updateWhitelist = async (otp: string, ips?: string) => {
  const r = await axios.post(endPoint("/account/api/whitelist"), { otp, ips });
  const data = await extract(r);
  return parseResult(data);
};

// Admin
export const fetchSettings = () =>
  axios.get(endPoint("/settings")).then(extract).then(parseResult);

export const saveSetting = (values: Record<string, any>) =>
  axios.post(endPoint("/settings"), { values }).then(extract).then(parseResult);

export const fetchApiUsersData = async (count = 10, offset = 0) => {
  const r = await axios.get(endPoint("/users", { count, offset }));
  return extract(r);
};

export const fetchApiSetFee = async (idp: string, fee: string) => {
  const r = await axios.post(endPoint("/users/set_system_fee", { idp, fee }));
  const data = await extract(r);
  return parseResult(data);
};

export const fetchApiSetFeeMax = async (idp: string, fee: string) => {
  const r = await axios.post(
    endPoint("/users/set_max_network_fee", { idp, fee })
  );
  const data = await extract(r);
  return parseResult(data);
};

export const get2FaChannel = () =>
  axios.get(endPoint("/get2faChannel")).then(extract).then(parseResult);

export const updateUserSetting = async (name: string, value: string) => {
  const r = await axios.post(endPoint("/account/settings"), { name, value });
  const data = await extract(r);
  return parseResult(data);
};

export const apiBumpFee = (txid?: string, fee?: string) =>
  axios
    .post(endPoint("/finance/bump_fee"), { txid, fee })
    .then(extract)
    .then(parseResult);

export const apiEstimateBump = (txid?: string) =>
  axios
    .get(endPoint("/finance/estimate_bump", { txid }))
    .then(extract)
    .then(parseResult);

export const cancelTransaction = (id: number) =>
  axios
    .post(endPoint("/finance/cancel_wr"), { id })
    .then(extract)
    .then(parseResult);

export const fetchSwapInfo = async (query: Record<string, any>) => {
  const r = await axios.get(endPoint("/swap/info", query));
  return extract(r);
};

export const fetchSwapLimits = async (from: string, to: string) => {
  const r = await axios.get(endPoint("/swap/limits", { from, to }));
  return extract(r);
};

export const createSwap = async (rate_id: string, fee_from: string) => {
  const r = await axios.post(endPoint("/swap"), { rate_id, fee_from });
  return extract(r);
};

export const fetchAssets = () =>
  axios.get(endPoint("/list/assets")).then(extract);

export const fetchCurrencyExchangeRate = (currency: string) =>
  axios.get(endPoint(`/exchange/rates/${currency}`)).then(extract);
export const fetchCurrenciesExchange = () =>
  axios.get(endPoint("/exchange/currencies")).then(extract);

//
// @param {object} params
// @param {string} params.rate
// @param {string} params.amountFiat
// @param {string} params.amountCrypto
// @param {string} params.currencyTo
// @returns {Promise<any>}
//
export const fetchCalculateExchange = (props: Record<string, any>) =>
  axios.get(endPoint("/exchange/calculate", props)).then(extract);

export type AddCounterpartyParams = {
  first_name?: string;
  last_name?: string;
  type?: string;
  dob?: string;
  place_of_birth?: string;
  address_country?: string;
  address_town?: string;
  address_post_code?: string;
  address_street?: string;
  address_sub_street?: string;
  id_doc_number?: string;
  id_doc_type?: string;
  id_doc_country?: string;
  id_doc_registration_authority?: string;
  residence_country?: string;
  payment_method_type?: string;
  payment_method_account_id?: string;
  lei_code?: string;
  company_type?: string;
  license_number?: string;
  full_name?: string;
  address?: string;
};

export const addCounterparty = async (data: AddCounterpartyParams) => {
  const r = await axios.post(endPoint("/add-counterparty"), data);
  const responseData = await extract(r);
  return parseResult(responseData);
};

export const editCounterparty = async (
  id: number,
  data: AddCounterpartyParams
) => {
  const r = await axios.put(
    endPoint(`/edit-counterparty/${encodeURIComponent(id)}`),
    data
  );
  const responseData = await extract(r);
  return parseResult(responseData);
};

export type DeleteCounterpartyParams = {
  id: number;
};

export const deleteCounterparty = async ({ id }: DeleteCounterpartyParams) => {
  const r = await axios.post(endPoint("/delete-counterparty"), { id });
  const responseData = await extract(r);
  return parseResult(responseData);
};

export type Counterparty = {
  id: number;
  first_name: string;
  last_name: string;
  type: string;
  dob: string;
  place_of_birth: string;
  address_country: string;
  address_town: string;
  address_post_code: string;
  address_street: string;
  address_sub_street: string;
  id_doc_number: string;
  id_doc_type: string;
  id_doc_country: string;
  id_doc_registration_authority: string;
  residence_country: string;
  payment_method_type: string;
  payment_method_account_id: string;
  external_user_id: string;
  client_id: string;
  chain: string;
  address: string;
};

export const fetchCounterparties = async () => {
  const r = await axios.get(endPoint("/get-counterparties"));
  const responseData = await extract(r);
  const parsed = parseResult(responseData) as {
    counterparties: Counterparty[];
  };
  return parsed.counterparties;
};

export const bulkRefreshBalances = async (details: BalanceResponseDto[]) => {
  const r = await axios.post(endPoint("/bulk-refresh-balances"), { details });
  return extract(r);
};

export type UserCompany = {
  id: number;
  parent_id: number;
  user_login: string;
  position: string | null;
  role: string | null;
  login: string | null;
  company_name: string | null;
};

export const fetchUserCompanies = async (): Promise<UserCompany[]> => {
  const r = await axios.get(endPoint("/companies"));
  const responseData = await extract(r);
  const parsed = parseResult(responseData) as {
    companies: UserCompany[];
  };
  return parsed.companies;
};

export const switchCompany = async (parent_id: number) => {
  await axios.post(endPoint("/switch-company"), { parent_id });
};

export interface FetchTransactionsParams {
  type: string;
  count?: number;
  offset?: number;
  asset?: string;
  from?: number;
  till?: number;
  num?: number;
  txid?: string;
  address?: string;
  onlyUnconfirmed?: boolean;
  onlySwaps?: boolean;
  onlyReview?: boolean;
}

export interface TransactionsResponse {
  transactions: Transaction[];
  total: number;
}

export const fetchTransactionsForUser = async (
  params: FetchTransactionsParams
): Promise<TransactionsResponse> => {
  const r = await axios.get(
    endPoint(`/finance/transactions/${params.type}`, params)
  );
  const responseData = await extract(r);
  const parsed = parseResult(responseData) as TransactionsResponse;
  return parsed;
};

export type GetPaymentSourceLinkParams = {
  num: number;
};

export const getPaymentSourceLink = async (
  data: GetPaymentSourceLinkParams
) => {
  const r = await axios.post(endPoint("/get-payment-source-link"), data);
  const responseData = await extract(r);
  return parseResult(responseData);
};

export type SubmitInboundTravelRuleParams = {
  counterpartyId: number;
  num: number;
};

export const submitInboundTravelRule = async (
  data: SubmitInboundTravelRuleParams
) => {
  const r = await axios.post(endPoint("/send-inbound-tr"), data);
  const responseData = await extract(r);
  return parseResult(responseData);
};

export interface PaginatedUsersWithBalancesResponseDto {
  total: number;
  usersWithBalances: UserWithBalancesResponseDto[];
  result: boolean;
}

export interface UserWithBalancesResponseDto {
  user: UserForTable;
  mainBalances: BalanceResponseDto[];
  otherBalances: BalanceResponseDto[];
  summaryBalances: BalanceResponseDto[];
}

export const fetchUsersForMainWallet = async (
  count: number = 10,
  offset: number = 0,
  sort: string = "updatedAt",
  direction: "ASC" | "DESC" = "DESC",
  registrationDateFrom: string | null = null,
  registrationDateTo: string | null = null,
  login: string = "",
  phone: string = "",
  clientId: string = "",
  fullName: string = ""
): Promise<PaginatedUsersWithBalancesResponseDto> => {
  return await axios
    .get<PaginatedUsersWithBalancesResponseDto>(
      endPoint("/users/list", {
        count,
        offset,
        sort,
        direction,
        registrationDateFrom,
        registrationDateTo,
        login,
        phone,
        clientId,
        fullName,
      })
    )
    .then(extract);
};
